/**
 * CommandPalette — ⌘K / Ctrl+K. Search the live-backed dashboard surfaces.
 *
 * Phase 7c — see docs/plans/2026-04-28-frontend-redesign.md §6.8.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

type CommandKind = 'page'

interface CommandItem {
  id: string
  kind: CommandKind
  label: string
  hint: string
  to: string
}

const KIND_LABEL: Record<CommandKind, string> = {
  page: 'Page',
}

function buildItems(): CommandItem[] {
  const items: CommandItem[] = [
    { id: 'page-posture', kind: 'page', label: 'Posture', hint: 'The affidavit', to: '/' },
    {
      id: 'page-performance',
      kind: 'page',
      label: 'Performance',
      hint: 'Equity, calibration, closed trades',
      to: '/performance',
    },
    {
      id: 'page-positions',
      kind: 'page',
      label: 'Positions',
      hint: 'What is held now',
      to: '/positions',
    },
    {
      id: 'page-discovery',
      kind: 'page',
      label: 'Discovery',
      hint: 'Funnel, blockers, opportunities',
      to: '/discovery',
    },
    {
      id: 'page-execution',
      kind: 'page',
      label: 'Execution',
      hint: 'Orders, fills, history',
      to: '/execution',
    },
    {
      id: 'page-activity',
      kind: 'page',
      label: 'Activity',
      hint: 'Autonomous live tape',
      to: '/activity',
    },
    {
      id: 'page-risk',
      kind: 'page',
      label: 'Risk',
      hint: 'Cash, Greeks, stress, correlation',
      to: '/risk',
    },
    {
      id: 'page-alerts',
      kind: 'page',
      label: 'Alerts',
      hint: 'Live unresolved warnings',
      to: '/alerts',
    },
    {
      id: 'page-system',
      kind: 'page',
      label: 'System',
      hint: 'Services, gates, audit trail',
      to: '/system',
    },
  ]
  return items
}

function fuzzyScore(query: string, label: string): number {
  if (!query) return 0
  const q = query.toLowerCase()
  const l = label.toLowerCase()
  if (l === q) return 1000
  if (l.startsWith(q)) return 900
  if (l.includes(q)) return 700
  // Subsequence match: every char in q in order in l
  let i = 0
  for (const ch of l) {
    if (ch === q[i]) i++
    if (i === q.length) return 400
  }
  return 0
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  if (!open) return null
  return <CommandPaletteDialog onClose={onClose} />
}

function CommandPaletteDialog({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  const allItems = useMemo(() => buildItems(), [])

  const filtered = useMemo(() => {
    if (!query) return allItems
    return allItems
      .map((item) => ({ item, score: fuzzyScore(query, item.label) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.item)
  }, [allItems, query])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const select = (item: CommandItem) => {
    onClose()
    navigate(item.to)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const choice = filtered[activeIndex]
      if (choice) select(choice)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      data-testid="command-palette"
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/70 backdrop-blur-sm pt-[18vh] sm:pt-[20vh]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-[560px] mx-4 border border-rule-2 bg-ink shadow-2xl">
        <div className="border-b border-rule px-4 py-3">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={onKeyDown}
            placeholder="Jump to a page, symbol, trade, or gate…"
            aria-label="Command palette input"
            data-testid="command-palette-input"
            className="w-full bg-transparent font-mono text-[13px] text-bone placeholder:text-dim focus:outline-none"
          />
        </div>
        <ul className="max-h-[50vh] overflow-y-auto" role="listbox">
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center font-mono text-[12px] italic text-muted">
              No matches.
            </li>
          ) : (
            filtered.map((item, i) => (
              <li
                key={item.id}
                role="option"
                aria-selected={i === activeIndex}
                data-testid={`command-item-${item.id}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => select(item)}
                className={`flex cursor-pointer items-baseline justify-between gap-4 px-4 py-2.5 font-mono text-[13px] ${
                  i === activeIndex ? 'bg-amber/15 text-bone' : 'text-bone hover:bg-rule/40'
                }`}
              >
                <div className="flex items-baseline gap-3">
                  <span className="w-12 shrink-0 text-[10px] uppercase tracking-[0.12em] text-dim">
                    {KIND_LABEL[item.kind]}
                  </span>
                  <span className="truncate">{item.label}</span>
                </div>
                <span className="shrink-0 text-[11px] text-muted">{item.hint}</span>
              </li>
            ))
          )}
        </ul>
        <div className="flex items-center justify-between border-t border-rule px-4 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-dim">
          <span>↑↓ navigate · ↵ select · esc close</span>
          <span>
            {filtered.length} {filtered.length === 1 ? 'result' : 'results'}
          </span>
        </div>
      </div>
    </div>
  )
}
